import { getExportCoverElements } from './exportCoverRefs';
import { serializeCss, serializeElements } from './serialize';

const BASE_PDF_CSS = `
.export-cover-page {
  break-inside: avoid;
  page-break-inside: avoid;
  page-break-after: always;
}
.exportable-element-container {
  break-inside: avoid;
  page-break-inside: avoid;
  position: relative;
  padding: 16px;
  overflow: visible;
}
.exportable-element {
  flex: 1;
  margin: 8px auto;
  overflow: visible;
}
.shadow-even {
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.25);
}
.external-header {
  display: flex!important;
  position: absolute;
  top: 16px;
  right: 16px;
  left: 16px;
  border-bottom: 1px solid #D7D8D8;
}
.hide-in-export {
  display: none;
}
.exportable-element-header {
  height: 60px!important;
}
`;

const BASE_JPEG_CSS = `
.exportable-element-container {
  break-inside: avoid;
  position: relative;
  padding: 0 16px;
}
.exportable-element {
  flex: 1;
  margin: 8px auto;
  max-width: 100% !important;
}
.exportable-element > div {
  height: auto !important;
  min-height: unset !important;
  max-width: 100% !important;
}
[data-test-chart-type] {
  height: auto !important;
  overflow: visible !important;
}
[data-test-chart-type] * {
  overflow: visible !important;
  max-height: none !important;
}
.hide-in-export {
  display: none;
}
.exportable-element-header {
  height: 60px!important;
}
`;

/**
 * Measure export dimensions, accounting for nested scrollable chart containers.
 */
export function measureJpegExportDimensions(sectionRoot: HTMLElement): {
  height: number;
  width: number;
} {
  const rootRect = sectionRoot.getBoundingClientRect();
  let maxHeight = sectionRoot.scrollHeight;
  let maxWidth = sectionRoot.scrollWidth;

  sectionRoot.querySelectorAll('*').forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    const rect = node.getBoundingClientRect();
    const relativeTop = rect.top - rootRect.top;
    const relativeLeft = rect.left - rootRect.left;

    if (node.scrollHeight > node.clientHeight + 1) {
      maxHeight = Math.max(maxHeight, relativeTop + node.scrollHeight);
    } else {
      maxHeight = Math.max(maxHeight, relativeTop + rect.height);
    }

    if (node.scrollWidth > node.clientWidth + 1) {
      maxWidth = Math.max(maxWidth, relativeLeft + node.scrollWidth);
    } else {
      maxWidth = Math.max(maxWidth, relativeLeft + rect.width);
    }
  });

  return {
    height: Math.ceil(Math.max(maxHeight, sectionRoot.clientHeight)),
    width: Math.ceil(Math.max(maxWidth, sectionRoot.clientWidth)),
  };
}

/**
 * Prefer the column that contains `ref` when `.exportable-element` wraps a multi-column grid.
 */
export function resolveExportSectionRoot(ref: HTMLElement): HTMLElement {
  const exportable =
    ref.closest && ref.closest('.exportable-element')
      ? (ref.closest('.exportable-element') as HTMLElement)
      : null;
  if (!exportable) {
    return ref;
  }

  const contentRoot = exportable.firstElementChild;
  if (!(contentRoot instanceof HTMLElement)) {
    return exportable;
  }

  // Insight cards attach the ref to the card container (direct child of exportable).
  if (ref === contentRoot) {
    return ref;
  }

  // Multi-column grid layouts use children with a span attribute (e.g. grid Col).
  const spanColumns = Array.from(contentRoot.children).filter(
    (child): child is HTMLElement =>
      child instanceof HTMLElement && child.hasAttribute('span')
  );
  if (spanColumns.length > 1) {
    const column = spanColumns.find((col) => col.contains(ref));
    return column ?? contentRoot;
  }

  // Sections with multiple siblings (e.g. overview header/content/footer).
  if (contentRoot.children.length > 1) {
    const matchingChild = Array.from(contentRoot.children).find(
      (child): child is HTMLElement =>
        child instanceof HTMLElement && child.contains(ref)
    );
    if (matchingChild) {
      return matchingChild;
    }
  }

  return contentRoot;
}

export function getExportableElements(tabInstanceId: string): HTMLElement[] {
  return [
    ...document.querySelectorAll(`#${tabInstanceId} .exportable-element`),
  ] as HTMLElement[];
}

export function subscribeExportWebsocket(exportId: string, runId: string) {
  window.dispatchEvent(
    new CustomEvent('SEND_WEBSOCKET_MESSAGE', {
      detail: {
        type: 'export',
        action: 'subscribe',
        main_id: exportId,
        run_id: runId,
      },
    })
  );
}

export type PrepareJpegExportOptions = {
  sectionRoot: HTMLElement;
  isOverview?: boolean;
  extraCss?: string;
  heightPadding?: number;
  widthPadding?: number;
  /** Override measured height (sectionRoot.clientHeight + padding). */
  height?: number;
  /** Override measured width (sectionRoot.clientWidth + padding). */
  width?: number;
  /** Inline style for the page container wrapping each block. */
  pageContainerStyle?: string;
};

export type PrepareJpegExportResult = {
  html: string;
  height: number;
  width: number;
};

export function prepareJpegExport({
  sectionRoot,
  isOverview = false,
  extraCss = '',
  heightPadding = 60,
  widthPadding = 60,
  height: heightOverride,
  width: widthOverride,
  pageContainerStyle = 'display: flex; align-items: flex-start; flex: 1; padding-top: 16px;',
}: PrepareJpegExportOptions): PrepareJpegExportResult {
  const measured = measureJpegExportDimensions(sectionRoot);
  const height = heightOverride ?? measured.height + heightPadding;
  const width = widthOverride ?? measured.width + widthPadding;
  const jpegScopedCss = serializeCss(sectionRoot);
  const serializedElements = serializeElements(
    [sectionRoot],
    isOverview,
    false,
    sectionRoot,
    jpegScopedCss
  );

  const pages = serializedElements.blocks.reduce((acc, block) => {
    return `${acc}<div class="exportable-element-container" style="${pageContainerStyle}">
           ${block.html}
        </div>`;
  }, '');

  const html = `
    <html>
      <head>
        <style>
          ${jpegScopedCss}
          ${BASE_JPEG_CSS}
          ${extraCss}
        </style>
      </head>
      <body style="height: ${height}px;">${pages}</body>
    </html>
  `;

  return { html, height, width };
}

export type PdfWidthSource = 'scroller' | 'exportable';

export type PreparePdfExportOptions = {
  tabInstanceId: string;
  header: string;
  cover: string;
  backCover: string;
  isOverview?: boolean;
  extraCss?: string;
  heightPadding?: number;
  widthPadding?: number;
  widthSource?: PdfWidthSource;
};

export type PreparePdfExportResult = {
  html: string;
  height: number;
  width: number;
  pagesNum: number;
};

export function preparePdfExport({
  tabInstanceId,
  header,
  cover,
  backCover,
  isOverview = false,
  extraCss = '',
  heightPadding = 500,
  widthPadding = 60,
  widthSource = 'scroller',
}: PreparePdfExportOptions): PreparePdfExportResult {
  const elements = getExportableElements(tabInstanceId);
  const coverElements = getExportCoverElements();
  const cssScope = [...elements, ...coverElements];

  let height = elements.reduce((max, elem) => {
    return Math.max(max, elem.clientHeight);
  }, 0);
  height += heightPadding;

  const widthSelector =
    widthSource === 'exportable'
      ? `#${tabInstanceId} .exportable-element`
      : `#${tabInstanceId} .hx-section-header-scroller`;
  const widthNode = document.querySelector(widthSelector) as HTMLElement | null;
  const width = (widthNode?.clientWidth || 0) + widthPadding;

  const scopedCss = serializeCss(cssScope);
  const serializedElements = serializeElements(
    elements,
    isOverview,
    false,
    cssScope,
    scopedCss
  );
  const pagesNum = serializedElements.blocks.length + 2;
  const pageHeight = pagesNum * height;

  const pages = serializedElements.blocks.reduce((acc, block) => {
    return `${acc}<div class="exportable-element-container" style="display: flex; align-items: center; flex: 1; padding-top: 16px; height: 100vh;">
          ${!block.hideHeader ? header : '<div></div>'}  
          ${block.html}
        </div>`;
  }, '');

  const coverPageStyle = `display: block; break-inside: avoid; page-break-inside: avoid; page-break-after: always; height: ${height}px; min-height: ${height}px; max-height: ${height}px; overflow: hidden; box-sizing: border-box;`;
  const coverWrapper = `<div class="export-cover-page" style="${coverPageStyle}">`;
  const coverWrapperEnd = '</div>';

  const html = `
    <html>
      <head>
        <style>
          ${scopedCss}
          ${BASE_PDF_CSS}
          ${extraCss}
        </style>
      </head>
      <body style="height: ${pageHeight}px; background-color: #ffffff;">${coverWrapper}${cover}${coverWrapperEnd}${pages}${coverWrapper}${backCover}${coverWrapperEnd}</body>
    </html>
  `;

  return { html, height, width, pagesNum };
}
